import {
  useCallback,
  createContext,
  type PropsWithChildren,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { projects as demoProjects } from "../lib/demo-data";
import { appConfig } from "../lib/config";
import { roleCan, type WorkspaceCapability } from "../lib/permissions";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import type { Project, Workspace, WorkspaceRole } from "../lib/types";
import { createWorkspace as createWorkspaceRecord } from "../lib/workspaces";

const DEMO_WORKSPACE_ID = "demo-workspace";

type WorkspaceProject = Project & {
  workspaceId: string;
};

type MembershipRow = {
  workspace_id: string;
  role: WorkspaceRole;
};

type WorkspaceRow = {
  id: string;
  name: string;
};

type WidgetSettingsRow = {
  assistant_name: string;
  primary_color: string;
};

type EnvironmentRow = {
  name: string;
  kind: "production" | "staging" | "development";
  enabled: boolean;
};

type ProjectRow = {
  id: string;
  workspace_id: string;
  public_id: string;
  name: string;
  status: "draft" | "active" | "suspended" | "archived";
  widget_settings: WidgetSettingsRow | WidgetSettingsRow[] | null;
  project_environments: EnvironmentRow[] | null;
};

type AccountData = {
  workspaces: Workspace[];
  projects: WorkspaceProject[];
  userName: string;
  userEmail: string;
  subscription: SubscriptionSummary | null;
};

export type SubscriptionSummary = {
  status:
    | "trialing"
    | "active"
    | "past_due"
    | "canceled"
    | "incomplete"
    | "unpaid"
    | "paused";
  trialEndsAt: string | null;
  observedAt: string;
  effectiveProjectLimit: number | null;
  projectCount: number;
  /**
   * Mirrors app_private.workspace_has_active_access: active plan, trial still
   * running, or past_due inside the grace window. The backend remains
   * authoritative; this only drives the read-only UI state.
   */
  accessActive: boolean;
};

type AppState = {
  workspaces: Workspace[];
  currentWorkspaceId: string | null;
  workspaceName: string;
  currentWorkspaceRole: WorkspaceRole | null;
  setCurrentWorkspaceId: (id: string) => void;
  createWorkspace: (name: string) => Promise<string>;
  can: (capability: WorkspaceCapability) => boolean;
  projects: Project[];
  currentProject: Project | null;
  setCurrentProjectId: (id: string) => void;
  refreshProjects: () => Promise<void>;
  loading: boolean;
  error: string | null;
  userName: string;
  userEmail: string;
  demoMode: boolean;
  subscriptionSummary: SubscriptionSummary | null;
};

const AppContext = createContext<AppState | undefined>(undefined);

function firstRelation<T>(value: T | T[] | null): T | undefined {
  return Array.isArray(value) ? value[0] : (value ?? undefined);
}

function projectStatus(status: ProjectRow["status"]): Project["status"] {
  if (status === "active") return "active";
  if (status === "draft") return "setup";
  if (status === "archived") return "archived";
  return "paused";
}

function environmentLabel(environment: EnvironmentRow | undefined): string {
  if (!environment) return "Sem instalação configurada";

  const kind = {
    production: "Produção",
    staging: "Homologação",
    development: "Desenvolvimento",
  }[environment.kind];

  if (environment.name === environment.kind) return kind;
  return `${environment.name} • ${kind}`;
}

function mapProject(row: ProjectRow): WorkspaceProject {
  const settings = firstRelation(row.widget_settings);
  const environments = row.project_environments ?? [];
  const environment =
    environments.find((item) => item.enabled && item.kind === "production") ??
    environments.find((item) => item.enabled);

  return {
    id: row.id,
    publicId: row.public_id,
    workspaceId: row.workspace_id,
    name: row.name,
    assistantName: settings?.assistant_name ?? "Assistente",
    color: settings?.primary_color ?? "#6D28D9",
    status: projectStatus(row.status),
    conversations: 0,
    users: 0,
    sourcedRate: 0,
    averageLatency: 0,
    knowledgeDocuments: 0,
    installation: environmentLabel(environment),
  };
}

function caughtMessage(caught: unknown): string {
  if (
    caught &&
    typeof caught === "object" &&
    "message" in caught &&
    typeof caught.message === "string"
  ) {
    return caught.message;
  }
  return "Não foi possível carregar os dados deste workspace.";
}

async function fetchAccountData(
  preferredWorkspaceId?: string | null,
): Promise<AccountData> {
  if (!supabase) {
    throw new Error("Supabase não está configurado.");
  }

  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user)
    throw new Error("Sessão expirada. Entre novamente.");
  const { data: membershipData, error: membershipError } = await supabase
    .from("workspace_members")
    .select("workspace_id, role")
    .eq("user_id", userData.user.id)
    .eq("status", "active")
    .order("created_at", { ascending: true });
  if (membershipError) throw membershipError;

  const memberships = (membershipData ?? []) as MembershipRow[];
  const workspaceIds = memberships.map((membership) => membership.workspace_id);
  const user = userData.user;
  const metadata = user.user_metadata;
  const userName = String(
    metadata.full_name || metadata.name || user.email || "Usuário",
  );

  if (workspaceIds.length === 0) {
    return {
      workspaces: [],
      projects: [],
      userName,
      userEmail: user.email ?? "",
      subscription: null,
    };
  }

  const [
    { data: workspaceData, error: workspaceError },
    { data: projectData, error: projectError },
  ] = await Promise.all([
    supabase
      .from("workspaces")
      .select("id, name")
      .in("id", workspaceIds)
      .eq("status", "active"),
    supabase
      .from("projects")
      .select(
        `
          id,
          workspace_id,
          public_id,
          name,
          status,
          widget_settings (
            assistant_name,
            primary_color
          ),
          project_environments (
            name,
            kind,
            enabled
          )
        `,
      )
      .in("workspace_id", workspaceIds)
      .order("created_at", { ascending: true }),
  ]);

  if (workspaceError) throw workspaceError;
  if (projectError) throw projectError;

  const workspacesById = new Map(
    ((workspaceData ?? []) as WorkspaceRow[]).map((workspace) => [
      workspace.id,
      workspace,
    ]),
  );
  const workspaces = memberships.flatMap((membership) => {
    const workspace = workspacesById.get(membership.workspace_id);
    return workspace
      ? [
          {
            id: workspace.id,
            name: workspace.name,
            role: membership.role,
          },
        ]
      : [];
  });

  const subscriptionWorkspaceId =
    preferredWorkspaceId && workspaceIds.includes(preferredWorkspaceId)
      ? preferredWorkspaceId
      : workspaceIds[0];

  return {
    workspaces,
    projects: ((projectData ?? []) as unknown as ProjectRow[]).map(mapProject),
    userName,
    userEmail: user.email ?? "",
    subscription: await workspaceSubscriptionSummary(subscriptionWorkspaceId),
  };
}

async function workspaceSubscriptionSummary(
  workspaceId: string | undefined,
): Promise<SubscriptionSummary | null> {
  if (!supabase || !workspaceId) return null;
  const { data, error } = await supabase.rpc("workspace_access_summary", {
    p_workspace_id: workspaceId,
  });
  if (error || !data)
    throw new Error(
      "Não foi possível consultar o acesso do workspace. Tente novamente.",
    );
  const summary = data as {
    status: SubscriptionSummary["status"];
    trial_ends_at: string | null;
    observed_at: string;
    access_active: boolean;
    project_limit: number | null;
    project_count: number;
  };
  return {
    status: summary.status,
    trialEndsAt: summary.trial_ends_at,
    observedAt: summary.observed_at,
    effectiveProjectLimit: summary.project_limit,
    projectCount: summary.project_count,
    accessActive: summary.access_active,
  };
}

export function AppProvider({ children }: PropsWithChildren) {
  const location = useLocation();
  const loadGeneration = useRef(0);
  const demoMode = appConfig.allowDemo && !isSupabaseConfigured;
  const [workspaces, setWorkspaces] = useState<Workspace[]>(() =>
    demoMode
      ? [
          {
            id: DEMO_WORKSPACE_ID,
            name: "Simplifica Gestão",
            role: "owner",
          },
        ]
      : [],
  );
  const [allProjects, setAllProjects] = useState<WorkspaceProject[]>(() =>
    demoMode
      ? demoProjects.map((project) => ({
          ...project,
          workspaceId: DEMO_WORKSPACE_ID,
        }))
      : [],
  );
  const [workspaceId, setWorkspaceId] = useState<string | null>(
    demoMode
      ? DEMO_WORKSPACE_ID
      : sessionStorage.getItem("4apex.preferredWorkspace"),
  );
  const [projectId, setProjectId] = useState(
    demoMode ? demoProjects[0].id : "",
  );
  const [loading, setLoading] = useState(!demoMode);
  const [error, setError] = useState<string | null>(null);
  const [userName, setUserName] = useState(
    demoMode ? "Ana Martins" : "Usuário",
  );
  const [userEmail, setUserEmail] = useState("");
  const [subscriptionSummary, setSubscriptionSummary] =
    useState<SubscriptionSummary | null>(null);

  const refreshProjects = useCallback(async () => {
    if (demoMode) return;

    const generation = ++loadGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAccountData(workspaceId);
      if (generation !== loadGeneration.current) return;
      setWorkspaces(data.workspaces);
      setAllProjects(data.projects);
      setUserName(data.userName);
      setUserEmail(data.userEmail);
      setSubscriptionSummary(data.subscription);
      setWorkspaceId((current) =>
        current && data.workspaces.some((workspace) => workspace.id === current)
          ? current
          : (data.workspaces[0]?.id ?? null),
      );
    } catch (caught) {
      if (generation !== loadGeneration.current) return;
      setError(caughtMessage(caught));
      setAllProjects([]);
      setSubscriptionSummary(null);
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [demoMode, workspaceId]);

  const createWorkspace = useCallback(
    async (name: string) => {
      if (demoMode) {
        throw new Error(
          "A criação de workspace não persiste no ambiente de demonstração.",
        );
      }
      const createdWorkspaceId = await createWorkspaceRecord(name);
      await refreshProjects();
      setWorkspaceId(createdWorkspaceId);
      return createdWorkspaceId;
    },
    [demoMode, refreshProjects],
  );

  useEffect(() => {
    void refreshProjects();
  }, [refreshProjects]);

  const currentWorkspace =
    workspaces.find((workspace) => workspace.id === workspaceId) ?? null;
  const can = useCallback(
    (capability: WorkspaceCapability) =>
      roleCan(currentWorkspace?.role, capability),
    [currentWorkspace?.role],
  );
  const projects = useMemo(
    () => allProjects.filter((project) => project.workspaceId === workspaceId),
    [allProjects, workspaceId],
  );
  const routeProjectId = location.pathname.match(
    /^\/app\/projects\/([^/]+)$/,
  )?.[1];
  const explicitProjectId =
    routeProjectId === "new"
      ? null
      : routeProjectId || new URLSearchParams(location.search).get("project");
  const routeWorkspaceId = explicitProjectId
    ? allProjects.find((p) => p.id === explicitProjectId)?.workspaceId
    : null;
  useEffect(() => {
    if (routeWorkspaceId && routeWorkspaceId !== workspaceId) {
      loadGeneration.current += 1;
      setLoading(!demoMode);
      setSubscriptionSummary(null);
      setWorkspaceId(routeWorkspaceId);
      sessionStorage.setItem("4apex.preferredWorkspace", routeWorkspaceId);
    }
  }, [routeWorkspaceId, workspaceId, demoMode]);
  const currentProject = explicitProjectId
    ? (projects.find((project) => project.id === explicitProjectId) ?? null)
    : (projects.find((project) => project.id === projectId) ??
      projects[0] ??
      null);

  const value = useMemo(
    () => ({
      workspaces,
      currentWorkspaceId: currentWorkspace?.id ?? null,
      workspaceName: currentWorkspace?.name ?? "Nenhum workspace",
      currentWorkspaceRole: currentWorkspace?.role ?? null,
      setCurrentWorkspaceId: (id: string) => {
        loadGeneration.current += 1;
        sessionStorage.setItem("4apex.preferredWorkspace", id);
        setProjectId("");
        setSubscriptionSummary(null);
        setLoading(!demoMode);
        setWorkspaceId(id);
      },
      createWorkspace,
      can,
      projects,
      currentProject,
      setCurrentProjectId: setProjectId,
      refreshProjects,
      loading,
      error,
      userName,
      userEmail,
      demoMode,
      subscriptionSummary,
    }),
    [
      currentProject,
      currentWorkspace,
      createWorkspace,
      can,
      demoMode,
      error,
      loading,
      projects,
      refreshProjects,
      subscriptionSummary,
      userEmail,
      userName,
      workspaces,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// This compact demo keeps the provider and its hook together.
// eslint-disable-next-line react-refresh/only-export-components
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
}
